import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper } from "@mui/material";
import { API_BASE } from "./utils/apiBase";

const KIOSK_CODE = "ENTRANCE-01";

function getRecognitionMessage(payload) {
  const code = String(payload?.code || "").trim().toUpperCase();
  if (code === "NO_FACE" || code === "NO_FACE_DETECTED") return "No face detected. Face the camera and try again.";
  if (code === "MULTIPLE_FACES") return "Multiple faces detected. Only one person should stand in front of the camera.";
  if (code === "NO_MATCH" || code === "FACE_NOT_RECOGNIZED") return "Face not recognized. Try again or register clearer face photos.";
  if (code === "ROLE_NOT_ALLOWED") return payload?.message || "This account cannot use kiosk attendance.";
  if (code === "BREAK_ACTIVE") return payload?.message || "This employee is currently on break.";
  if (code === "ACCOUNT_INACTIVE") return payload?.message || "This account is inactive.";
  if (payload?.message) return payload.message;
  return "Recognition failed. Please try again.";
}

function formatActionLabel(action) {
  return action === "time_out" ? "Time Out" : "Time In";
}

function formatKioskDateTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

export default function KioskAttendancePage({ user, onLogout }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const resetTimerRef = useRef(null);
  const requestTimeoutRef = useRef(null);
  const requestAbortRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mirror, setMirror] = useState(true);
  const [statusMessage, setStatusMessage] = useState("Entrance kiosk ready. Stand in front of the camera, then press Capture & Recognize.");
  const [statusTone, setStatusTone] = useState("neutral");
  const [lastResult, setLastResult] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState("");

  const kioskOperator = useMemo(() => {
    const code = String(user?.employeeid || user?.username || "").trim();
    return code || "HR kiosk";
  }, [user]);

  useEffect(() => {
    let active = true;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraReady(true);
      } catch (error) {
        console.error("[kiosk] camera start failed", error);
        setStatusTone("error");
        if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") {
          setStatusMessage("No camera device was found. Connect a camera and refresh this page.");
        } else if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
          setStatusMessage("Camera permission was denied. Allow camera access and refresh this page.");
        } else {
          setStatusMessage("Camera access failed. Check the camera connection, allow camera permission, and refresh this page.");
        }
      }
    };
    startCamera();
    return () => {
      active = false;
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }
      if (requestAbortRef.current) {
        try { requestAbortRef.current.abort(); } catch (_) {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const clearRequestGuard = () => {
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
    requestAbortRef.current = null;
  };

  const startRequestGuard = (controller, timeoutMessage) => {
    clearRequestGuard();
    requestAbortRef.current = controller;
    requestTimeoutRef.current = setTimeout(() => {
      try { controller.abort(); } catch (_) {}
      setBusy(false);
      setStatusTone("error");
      setStatusMessage(timeoutMessage);
    }, 60000);
  };

  const queueReset = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setLastResult(null);
      setStatusTone("neutral");
      setStatusMessage("Ready for the next employee.");
    }, 4500);
  };

  const capturePhotoBlob = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement("canvas");
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) throw new Error("Capture failed");
    return blob;
  };

  const fetchRegisteredPhotoUrl = async (employeeId) => {
    try {
      const response = await axios.get(`${API_BASE}/api/photos_blob/list/${encodeURIComponent(employeeId)}`, {
        withCredentials: true,
        validateStatus: () => true,
      });
      const photos = Array.isArray(response?.data?.photos) ? response.data.photos : [];
      const firstPhotoId = photos[0]?.id || photos[0]?.photo_id || photos[0]?.photoId || null;
      if (!firstPhotoId) return "";
      return `${API_BASE}/api/photos_blob/image/${encodeURIComponent(firstPhotoId)}`;
    } catch (error) {
      console.error("[kiosk] fetchRegisteredPhotoUrl failed", error);
      return "";
    }
  };

  const resetPendingConfirmation = () => {
    setPendingConfirmation(null);
    setPendingBlob(null);
    if (capturedPreviewUrl) {
      URL.revokeObjectURL(capturedPreviewUrl);
      setCapturedPreviewUrl("");
    }
  };

  const captureAndRecognize = async () => {
    if (busy || !cameraReady || !videoRef.current) return;
    setBusy(true);
    const controller = new AbortController();
    startRequestGuard(controller, "Recognition timed out after 60 seconds. You can try again now.");
    try {
      const blob = await capturePhotoBlob();
      const previewUrl = URL.createObjectURL(blob);
      if (capturedPreviewUrl) {
        URL.revokeObjectURL(capturedPreviewUrl);
      }

      const form = new FormData();
      form.append("photo", blob, "kiosk-capture.jpg");
      form.append("kiosk_code", KIOSK_CODE);
      form.append("mode", "preview");

      const response = await axios.post(`${API_BASE}/api/kiosk/scan`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
        validateStatus: () => true,
        signal: controller.signal,
      });
      const data = response?.data || {};

      if (response.status >= 200 && response.status < 300 && data.ok && data.requires_confirmation) {
        const registeredPhotoUrl = await fetchRegisteredPhotoUrl(data?.user?.employeeid || data?.user?.employee_no || data?.user?.username || "");
        setPendingBlob(blob);
        setCapturedPreviewUrl(previewUrl);
        setPendingConfirmation({
          action: data?.action || "time_in",
          employeeLabel: `${data?.user?.full_name || "Unknown"} (${data?.user?.employeeid || data?.user?.employee_no || "N/A"})`,
          employeeId: data?.user?.employeeid || data?.user?.employee_no || "N/A",
          fullName: data?.user?.full_name || "Unknown",
          role: data?.user?.role || "",
          confidence: typeof data?.recognition?.confidence === "number" ? data.recognition.confidence : null,
          kioskLabel: data?.kiosk?.location_label || "Entrance Camera",
          registeredPhotoUrl,
          scannedAt: new Date().toISOString(),
        });
        setStatusTone("neutral");
        setStatusMessage(data?.message || "Confirm the matched employee before saving attendance.");
        setLastResult(null);
      } else {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setLastResult(null);
        setStatusTone("error");
        setStatusMessage(getRecognitionMessage(data));
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || axios.isCancel?.(error)) {
        return;
      }
      console.error("[kiosk] captureAndRecognize failed", error);
      setLastResult(null);
      setStatusTone("error");
      setStatusMessage("Kiosk recognition failed. Please try again.");
    } finally {
      clearRequestGuard();
      setBusy(false);
    }
  };

  const confirmAttendance = async () => {
    if (busy || !pendingBlob || !pendingConfirmation) return;
    setBusy(true);
    const controller = new AbortController();
    startRequestGuard(controller, "Attendance confirmation timed out after 60 seconds. You can try again now.");
    try {
      const form = new FormData();
      form.append("photo", pendingBlob, "kiosk-capture.jpg");
      form.append("kiosk_code", KIOSK_CODE);
      form.append("mode", "confirm");

      const response = await axios.post(`${API_BASE}/api/kiosk/scan`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
        validateStatus: () => true,
        signal: controller.signal,
      });
      const data = response?.data || {};

      if (response.status >= 200 && response.status < 300 && data.ok) {
        const employeeLabel = `${data?.user?.full_name || "Unknown"} (${data?.user?.employeeid || data?.user?.employee_no || "N/A"})`;
        setLastResult({
          action: data?.action || "time_in",
          employeeLabel,
          role: data?.user?.role || "",
          confidence: typeof data?.recognition?.confidence === "number" ? data.recognition.confidence : null,
          kioskLabel: data?.kiosk?.location_label || "Entrance Camera",
        });
        setStatusTone("success");
        setStatusMessage(data?.message || `${employeeLabel} attendance saved.`);
        resetPendingConfirmation();
        queueReset();
      } else {
        setStatusTone("error");
        setStatusMessage(getRecognitionMessage(data));
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || axios.isCancel?.(error)) {
        return;
      }
      console.error("[kiosk] confirmAttendance failed", error);
      setStatusTone("error");
      setStatusMessage("Failed to confirm kiosk attendance. Please try again.");
    } finally {
      clearRequestGuard();
      setBusy(false);
    }
  };

  const toneStyles = {
    neutral: { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" },
    success: { background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" },
    error: { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)", padding: 24 }}>
      <Paper elevation={3} style={{ maxWidth: 1280, margin: "0 auto", borderRadius: 24, overflow: "hidden" }}>
        <div style={{ background: "#1d4ed8", color: "#fff", padding: "20px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 0.2 }}>Entrance Attendance Kiosk</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ background: "rgba(255,255,255,0.14)", borderRadius: 999, padding: "10px 16px", fontSize: 13 }}>
              Operator: {kioskOperator}
            </div>
            <Button variant="contained" color="error" onClick={onLogout}>Logout</Button>
          </div>
        </div>

        <div style={{ padding: 28, display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(320px, 1fr)", gap: 24 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ borderRadius: 24, overflow: "hidden", background: "#0f172a", position: "relative", minHeight: 480 }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  transform: mirror ? "scaleX(-1)" : "none",
                  minHeight: 480,
                }}
              />
              <div style={{ position: "absolute", top: 20, left: 20, padding: "8px 14px", borderRadius: 999, background: "rgba(15,23,42,0.72)", color: "#fff", fontSize: 13, fontWeight: 600 }}>
                {cameraReady ? `Kiosk ${KIOSK_CODE}` : "Starting camera..."}
              </div>
            </div>

            <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Button variant="contained" size="large" disabled={busy || !cameraReady} onClick={captureAndRecognize}>
                {busy ? "Scanning..." : "Capture & Recognize"}
              </Button>
              <Button variant="outlined" size="large" onClick={() => setMirror((prev) => !prev)}>
                {mirror ? "Mirror Preview" : "Unmirror Preview"}
              </Button>
            </div>
          </div>

          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ borderRadius: 20, padding: 18, ...toneStyles[statusTone] }}>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>Status</div>
              <div style={{ marginTop: 10, fontSize: 16, fontWeight: 600, lineHeight: 1.45 }}>{statusMessage}</div>
            </div>

            <Paper variant="outlined" style={{ borderRadius: 20, padding: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>Current Mode</div>
              <div style={{ marginTop: 8, fontSize: 22, fontWeight: 800, color: "#0f172a" }}>Shared Entrance Camera</div>
            </Paper>

            <Paper variant="outlined" style={{ borderRadius: 20, padding: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>Last Scan</div>
              {lastResult ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: "inline-flex", padding: "8px 12px", borderRadius: 999, background: lastResult.action === "time_out" ? "#fef3c7" : "#dcfce7", color: lastResult.action === "time_out" ? "#92400e" : "#166534", fontWeight: 700, fontSize: 13 }}>
                    {formatActionLabel(lastResult.action)}
                  </div>
                  <div style={{ marginTop: 14, fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>{lastResult.employeeLabel}</div>
                  <div style={{ marginTop: 8, color: "#475569", fontSize: 14 }}>Role: {lastResult.role || "N/A"}</div>
                  <div style={{ marginTop: 6, color: "#475569", fontSize: 14 }}>Location: {lastResult.kioskLabel}</div>
                </div>
              ) : (
                <div style={{ marginTop: 12, color: "#64748b", fontSize: 14 }}>No successful scan yet.</div>
              )}
            </Paper>
          </div>
        </div>
      </Paper>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <Dialog open={Boolean(pendingConfirmation)} onClose={busy ? undefined : resetPendingConfirmation} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Matched Account</DialogTitle>
        <DialogContent dividers>
          {pendingConfirmation ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px, 0.9fr) minmax(280px, 1.2fr) minmax(220px, 0.9fr)",
                gap: 18,
                alignItems: "stretch",
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Captured Face</div>
                <div style={{ borderRadius: 18, overflow: "hidden", background: "#e2e8f0", minHeight: 260, border: "1px solid #cbd5e1" }}>
                  {capturedPreviewUrl ? (
                    <img src={capturedPreviewUrl} alt="Captured employee" style={{ width: "100%", display: "block", objectFit: "cover", minHeight: 260 }} />
                  ) : null}
                </div>
              </div>

              <div
                style={{
                  borderRadius: 22,
                  border: "1px solid #dbeafe",
                  background: "linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)",
                  padding: 22,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 260,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: 0.6 }}>
                    Attendance Validation
                  </div>
                  <div style={{ marginTop: 14, fontSize: 38, fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>
                    {pendingConfirmation.employeeId}
                  </div>
                  <div style={{ marginTop: 12, height: 1, background: "#bfdbfe" }} />
                  <div style={{ marginTop: 16, fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1.25 }}>
                    {pendingConfirmation.fullName}
                  </div>
                  <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
                    <div style={{ color: "#334155", fontSize: 14 }}><strong>Role:</strong> {pendingConfirmation.role || "N/A"}</div>
                    <div style={{ color: "#334155", fontSize: 14 }}><strong>Action:</strong> {formatActionLabel(pendingConfirmation.action)}</div>
                    <div style={{ color: "#334155", fontSize: 14 }}><strong>Date & Time:</strong> {formatKioskDateTime(pendingConfirmation.scannedAt) || "N/A"}</div>
                    <div style={{ color: "#334155", fontSize: 14 }}><strong>Location:</strong> {pendingConfirmation.kioskLabel}</div>
                  </div>
                </div>
                <div style={{ marginTop: 18, padding: "12px 14px", borderRadius: 14, background: "#dbeafe", color: "#1e3a8a", fontSize: 14, fontWeight: 600 }}>
                  Please confirm that this is the correct account before saving attendance.
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Registered Photo</div>
                <div style={{ borderRadius: 18, overflow: "hidden", background: "#e2e8f0", minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #cbd5e1" }}>
                  {pendingConfirmation.registeredPhotoUrl ? (
                    <img src={pendingConfirmation.registeredPhotoUrl} alt="Registered employee" style={{ width: "100%", display: "block", objectFit: "cover", minHeight: 260 }} />
                  ) : (
                    <div style={{ color: "#64748b", fontSize: 14, padding: 16, textAlign: "center" }}>No registered face photo found.</div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: "14px 20px 18px" }}>
          <Button onClick={resetPendingConfirmation} disabled={busy} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmAttendance} disabled={busy || !pendingConfirmation} sx={{ minWidth: 180 }}>
            {busy ? "Saving..." : "Confirm Attendance"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

